import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Info } from "lucide-react";

// Tier descriptions for info buttons
export const TIER_INFO = {
  BRONZE: {
    name: "Bronze",
    description: "Entry-level tier for small businesses",
    features: [
      "1 active campaign",
      "Basic targeting options",
      "Standard support",
      "Basic analytics"
    ],
    color: "bg-amber-600"
  },
  SILVER: {
    name: "Silver",
    description: "Growing businesses with moderate ad spend",
    features: [
      "Up to 5 active campaigns",
      "Advanced targeting (time, demographics)",
      "Priority support",
      "Enhanced analytics with exports",
      "Deal/offer capabilities"
    ],
    color: "bg-gray-400"
  },
  GOLD: {
    name: "Gold",
    description: "Premium tier for high-volume advertisers",
    features: [
      "Unlimited campaigns",
      "Full targeting suite including competitor redirection",
      "Dedicated account manager",
      "Real-time analytics & API access",
      "Custom integrations",
      "Priority ad placement"
    ],
    color: "bg-yellow-500"
  }
};

// Delivered Customers metric explanations.
//
// "Drop Offs" was the previous name and should never have been: the thing being counted
// is a customer who arrived, and Delivered Customers says that.
//
// The two outcomes are "Direct To Door" and "Nearby Influenced" in full (round 4, G-2).
// The METRES are deliberately absent: the passenger app decides which event type it
// sends and the CRM bills what it receives, so a distance here is the CRM asserting a
// rule it does not own - and the numbers disagreed with Tier Pricing anyway, 200m here
// against 250m there, so the product was quoting two figures for one meter. Two outcomes, not three -
// Prompted was absorbed into Direct on 2026-09-03 and is no longer separately charged,
// so it is no longer separately explained. A tooltip describing a charge that does not
// exist is worse than no tooltip.
export const CPDC_INFO = {
  title: "Delivered Customers",
  description: "You're charged when a customer arrives at or near your business, based on the outcomes below.",
  types: [
    {
      name: "Direct To Door",
      description: "The customer saw or engaged with your ad and was dropped off at your business. This includes arrivals that came from a Booma recommendation."
    },
    {
      name: "Nearby Influenced",
      description: "The customer saw or engaged with your ad and was then dropped off close to your business. Influenced foot traffic rather than a confirmed arrival at the door."
    }
  ],
  footer: "Attribution windows are designed to reflect real customer intent and maximise meaningful outcomes for your business."
};

// What counts as a Click, and what one placement can cost (#17).
//
// Enforced by claim_placement_click (routes/ad_serving.py:98): an atomic insert keyed on
// the placement's tracking token, so the first click-type interaction wins and the rest
// are recorded without charging. Retries and double-taps cannot bill twice.
//
// "Follow venue" is listed as FREE rather than as a third click type. Round 2 grouped it
// with the other two, but favouriting claims no click and has no rate - it is the only
// interaction Bronze has at all. Describing it as billable would make an explainer about
// charges wrong about a charge.
export const CLICKS_INFO = {
  title: "Clicks",
  // No opening sentence. Round 4, G-14 asked for the previous one to be removed
  // outright, and the three outcomes carry the explanation without it.
  description: null,
  types: [
    {
      name: "Open offer",
      description: "The customer clicks your ad to view or redeem your offer.",
    },
    {
      name: "Save offer",
      description: "The customer taps to save your offer to their profile, to use at a later date.",
    },
    {
      name: "Follow venue",
      description: "The customer favourites your venue, saving your business to their profile to see all your future offers.",
    },
  ],
  footer: "One click is charged at most per offer placement: a customer who opens your offer to view or redeem it, saves it, and favourites your business is charged as one click, not three. If an ad is shown to them again from the same campaign, that is a new placement, and a click there is a new billable click.",
};

// Reusable clickable info button component
export const InfoButton = ({ content, className = "" }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className={`ml-1 text-muted-foreground hover:text-foreground transition-colors ${className}`}>
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="max-w-xs">
      <p className="text-sm">{content}</p>
    </PopoverContent>
  </Popover>
);

// Tier info badge with popover
export const TierInfoBadge = ({ tier }) => {
  const info = TIER_INFO[tier] || TIER_INFO.BRONZE;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="ml-1 text-muted-foreground hover:text-foreground transition-colors cursor-help">
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-sm" side="bottom" sideOffset={5}>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${info.color}`} />
            <span className="font-bold">{info.name} Tier</span>
          </div>
          <p className="text-sm text-muted-foreground">{info.description}</p>
          <ul className="text-xs space-y-1 mt-2">
            {info.features.map((feature, i) => (
              <li key={i} className="flex items-center gap-1">
                <span className="text-green-500">✓</span> {feature}
              </li>
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// One popover, any metric. Written once rather than copied for Clicks (#17): the two
// datasets have the same shape, and a second hand-maintained copy of this JSX is how the
// CPDC and Clicks explainers would drift apart.
export const MetricExplainerButton = ({ info, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={`About ${info.title}`}
        data-testid={testId}
      >
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="max-w-md">
      <div className="space-y-3">
        <div>
          <span className="font-bold">{info.title}</span>
          {/* Clicks has no opening line any more (G-14), so this must not render an
              empty paragraph and leave a gap above the first outcome. */}
          {info.description && (
            <p className="text-xs text-muted-foreground mt-1">{info.description}</p>
          )}
        </div>
        <div className="space-y-2">
          {info.types.map((type, i) => (
            <div key={i} className="p-2 rounded bg-secondary/50">
              <span className="font-medium text-sm">{type.name}</span>
              <p className="text-xs text-muted-foreground">{type.description}</p>
            </div>
          ))}
        </div>
        {info.footer && (
          <p className="text-xs text-muted-foreground italic mt-2 pt-2 border-t border-border">{info.footer}</p>
        )}
      </div>
    </PopoverContent>
  </Popover>
);

export const CPDCInfoButton = () => (
  <MetricExplainerButton info={CPDC_INFO} testId="cpdc-info-btn" />
);

export const ClicksInfoButton = () => (
  <MetricExplainerButton info={CLICKS_INFO} testId="clicks-info-btn" />
);

// The single stored-key -> display-label mapping.
//
// Booma had four names for the same idea at once: "Delivered Customers", "Arrivals"
// (this map, previously), "CPDC" (the stored keys) and "Store Visits" (the admin
// attribution stack). The agreed vocabulary is Views / Clicks / Delivered Customers /
// Redeem, and
// this is the one place that translates a stored key into it. Anything rendering a
// ledger entry type or a metric key should come through here rather than hard-coding a
// label, which is how the four names accumulated.
//
// Keys are the stored values - EventType and LedgerEntryType members, plus the legacy
// CPM/CPC short forms. They keep the CPDC_ prefix until the pre-go-live field rename;
// only the labels have moved.
export const METRIC_LABELS = {
  CPM: "Views",
  IMPRESSION: "Views",
  CPC: "Clicks",
  CLICK: "Clicks",
  SAVE_TO_CART: "Clicks",          // a save is a click type, billed as one
  CPDC: "Delivered Customers",
  CPDC_DIRECT: "Delivered Customer, Direct To Door",
  CPDC_PROXIMITY: "Delivered Customer, Nearby Influenced",
  CPDC_ORGANIC: "Delivered Customer, Organic",   // tracked, never billed
  // Retired as a meter (2026-09-03), kept as a LABEL so historical ledger entries
  // and invoices still render with a name rather than a raw enum.
  CPDC_PROMPTED: "Delivered Customer, Direct To Door",
  DEAL_REDEMPTION: "Redeem",
  ADJUSTMENT: "Adjustment",
  REFUND: "Refund"
};

export const getMetricLabel = (metricType) => {
  return METRIC_LABELS[metricType] || metricType;
};
